import { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { track } from "@/lib/api";
import {
  ArrowLeft,
  ArrowRight,
  HandsPraying,
  Quotes,
  Sparkle,
  Pause,
  Play,
  ArrowCounterClockwise,
  SpeakerHigh,
  SpeakerSlash,
  MusicNote,
  CheckCircle,
  Trash,
  HeartStraight,
} from "@phosphor-icons/react";

const STEP_LABEL = {
  arrive:    "Arrive",
  name:      "Name",
  bring:     "Bring",
  scripture: "Scripture",
  reflect:   "Reflect",
  surrender: "Surrender",
  rest:      "Rest",
};

// Ambient music URL is intentionally NOT hardcoded — set it via env so the
// asset lives on YOUR infrastructure (DigitalOcean Spaces / your CDN). If the
// env var is empty, the music toggle is hidden and the journey works
// silently. Add a track URL like:
//   REACT_APP_AMBIENT_MUSIC_URL=https://yourbucket.nyc3.cdn.digitaloceanspaces.com/voice-ambient.mp3
const MUSIC_TRACK = process.env.REACT_APP_AMBIENT_MUSIC_URL || "";
const MUSIC_AVAILABLE = !!MUSIC_TRACK;

const VOICE_PREF_KEY = "vg_personal_voice_id";

const MUSIC_MODES = [
  { id: "peaceful",  label: "Peaceful"  },
  { id: "deep",      label: "Deep"      },
  { id: "gentle",    label: "Gentle"    },
  { id: "stillness", label: "Stillness" },
  { id: "hopeful",   label: "Hopeful"   },
];

export default function PersonalPrayerPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [j, setJ] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(0);

  // TTS state
  const [voiceMode, setVoiceMode] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const audioRef = useRef(null);

  // Ambient music
  const [musicOn, setMusicOn] = useState(false);
  const [musicMode, setMusicMode] = useState("peaceful");
  const [musicVol, setMusicVol] = useState(0.18);
  const musicRef = useRef(null);

  // Voice catalog + persisted user pick.
  const [voices, setVoices] = useState([]);
  const [voiceId, setVoiceId] = useState(() => {
    if (typeof window === "undefined") return "";
    return window.localStorage.getItem(VOICE_PREF_KEY) || "";
  });

  // Share status (when this journey was shared anonymously to a circle).
  const [shareStatus, setShareStatus] = useState(null);

  useEffect(() => {
    api.get(`/prayer/personal/${id}`)
      .then((r) => { setJ(r.data); setStep(r.data.current_step || 0); })
      .catch(() => toast.error("Couldn't load journey"))
      .finally(() => setLoading(false));
    track("personal_prayer_open", { id });

    // Load curated voice catalog (falls back silently if voice service off).
    api.get("/voice/config").then((r) => {
      setVoices(r.data?.voices || []);
      // Initialize picker default from server if user hasn't chosen one yet.
      setVoiceId((cur) => cur || r.data?.default_voice_id || "");
    }).catch(() => {});

    // Share status — populates the "X people are praying for you" banner.
    api.get(`/prayer/personal/${id}/share-status`).then((r) => {
      if (r.data?.shared) setShareStatus(r.data);
    }).catch(() => {});
  }, [id]);

  // Persist progress on step change
  useEffect(() => {
    if (!j) return;
    api.post(`/prayer/personal/${id}/progress`, { step }).catch(() => {});
  }, [step, j, id]);

  // Cleanup audio on unmount.
  useEffect(() => {
    return () => {
      audioRef.current?.pause();
      musicRef.current?.pause();
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
    // eslint-disable-next-line
  }, []);

  const advance = () => {
    if (!j) return;
    const next = step + 1;
    stopVoice();
    if (next >= j.steps.length) {
      api.post(`/prayer/personal/${id}/complete`).catch(() => {});
      track("personal_prayer_completed", { id });
      setStep(j.steps.length); // signal completion view
    } else {
      track("personal_prayer_step_advanced", { id, step: next });
      setStep(next);
    }
  };

  const back = () => {
    if (step > 0) {
      stopVoice();
      setStep(step - 1);
    }
  };

  const restart = () => {
    stopVoice();
    setStep(0);
  };

  const remove = async () => {
    if (!window.confirm("Delete this journey? This can't be undone.")) return;
    try {
      await api.delete(`/prayer/personal/${id}`);
      toast("Journey removed.");
      navigate("/guided-prayer");
    } catch { toast.error("Couldn't delete"); }
  };

  // ---------- Voice TTS ----------

  const playVoiceForStep = async () => {
    if (!j || step >= j.steps.length) return;
    const s = j.steps[step];
    const lines = [];
    lines.push(s.title);
    if (s.scripture?.text) {
      lines.push(`${s.scripture.text} — ${s.scripture.reference}`);
    }
    lines.push(s.body);
    const text = lines.join(". ");

    setSpeaking(true);
    setAudioLoading(true);
    try {
      // Duck the music while speaking
      if (musicRef.current) musicRef.current.volume = Math.max(0.06, musicVol * 0.35);
      const r = await api.post("/voice/speak", { text, voice_id: voiceId || undefined });
      const b64 = r.data.audio_base64;
      const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
      const blob = new Blob([bytes], { type: r.data.mime_type || "audio/mpeg" });
      const url = URL.createObjectURL(blob);
      setAudioUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return url; });
      const a = audioRef.current || new Audio();
      audioRef.current = a;
      a.src = url;
      a.onended = () => {
        setSpeaking(false);
        if (musicRef.current) musicRef.current.volume = musicVol;
      };
      a.onerror = () => {
        setSpeaking(false);
        if (musicRef.current) musicRef.current.volume = musicVol;
        toast.error("Voice playback failed");
      };
      setAudioLoading(false);
      await a.play();
      track("personal_prayer_voice_played", { id, step });
    } catch (e) {
      setSpeaking(false);
      setAudioLoading(false);
      if (musicRef.current) musicRef.current.volume = musicVol;
      const detail = e?.response?.data?.detail || "Voice unavailable right now.";
      toast.error(detail);
    }
  };

  const stopVoice = () => {
    audioRef.current?.pause();
    setSpeaking(false);
    setAudioLoading(false);
    if (musicRef.current) musicRef.current.volume = musicVol;
  };

  // Auto-play when entering voice-mode and step changes
  useEffect(() => {
    if (voiceMode && j && step < j.steps.length) playVoiceForStep();
    else stopVoice();
    // eslint-disable-next-line
  }, [voiceMode, step]);

  // ---------- Ambient music ----------

  // Ambient music — only initialize when a track URL is configured.
  useEffect(() => {
    if (!MUSIC_AVAILABLE) return;
    if (!musicRef.current) {
      const m = new Audio(MUSIC_TRACK);
      m.loop = true;
      m.volume = musicVol;
      musicRef.current = m;
    }
    if (musicOn) {
      musicRef.current.volume = musicVol;
      musicRef.current.play().catch(() => {
        toast("Tap the music toggle once more to enable ambient sound.");
        setMusicOn(false);
      });
      track("personal_prayer_music_on", { mode: musicMode });
    } else {
      musicRef.current.pause();
    }
    return () => {};
    // eslint-disable-next-line
  }, [musicOn]);

  useEffect(() => {
    if (musicRef.current) musicRef.current.volume = musicVol;
  }, [musicVol]);

  if (loading) return <div className="px-5 py-10 text-center text-[#8B7355] font-body text-sm">Loading…</div>;
  if (!j) return (
    <div className="px-5 py-10 text-center" data-testid="journey-missing">
      <p className="text-[#5C4033] font-body text-sm">This journey is no longer available.</p>
      <Link to="/guided-prayer" className="text-[#C4A265] underline">Back</Link>
    </div>
  );

  const isComplete = step >= j.steps.length;
  const total = j.steps.length;
  const cur = isComplete ? null : j.steps[step];

  return (
    <div className="px-5 md:px-8 py-4 max-w-2xl mx-auto" data-testid="personal-prayer-page">
      <Link to="/guided-prayer" className="inline-flex items-center gap-1 text-[#8B7355] text-xs font-body mb-3">
        <ArrowLeft size={14} /> Journeys
      </Link>

      {/* Header */}
      <div className="mb-5" data-testid="journey-header">
        <p className="text-[#C4A265] text-[10px] uppercase tracking-[0.25em] font-body mb-1 inline-flex items-center gap-1.5">
          <Sparkle size={10} weight="fill" /> Personalized · {j.duration_min} min
        </p>
        <h1 className="font-heading text-2xl sm:text-3xl text-[#2D1B0E] font-medium leading-snug">
          {j.title}
        </h1>
        {j.theme && <p className="text-[#8B7355] font-body text-xs italic mt-1">{j.theme}</p>}
      </div>

      {/* Share banner — visible if this journey was shared with a circle. */}
      {shareStatus?.shared && (
        <Link
          to={`/circles/${shareStatus.circle_slug}`}
          onClick={() => track("personal_prayer_share_banner_click", { share_id: shareStatus.share_id })}
          data-testid="share-banner"
          className="block mb-4 group"
        >
          <div className="rounded-xl border-2 border-[#C4A265]/40 bg-gradient-to-br from-[#FAF5EC] to-[#F5EDE0] p-3.5 transition-all group-hover:-translate-y-0.5">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-white/70 flex items-center justify-center flex-shrink-0">
                <HeartStraight size={16} weight="fill" className="text-[#C4A265]" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[#5C4033] text-xs font-body leading-snug">
                  {shareStatus.response_count > 0 ? (
                    <><span className="font-medium text-[#2D1B0E]">{shareStatus.response_count} {shareStatus.response_count === 1 ? "person is" : "people are"}</span> praying for you.</>
                  ) : (
                    <>Your prayer was shared anonymously. Others are praying with you.</>
                  )}
                </p>
                <p className="text-[10px] text-[#8B7355] font-body italic">
                  {shareStatus.circle_name} circle ·  open prayers from your circle →
                </p>
              </div>
            </div>
          </div>
        </Link>
      )}

      {/* Mode controls */}
      <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="mode-controls">
        <button
          onClick={() => setVoiceMode((v) => !v)}
          data-testid="voice-mode-toggle"
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-body border ${
            voiceMode ? "bg-[#2D1B0E] text-white border-[#2D1B0E]" : "bg-white/60 border-[#E8DFD3] text-[#5C4033] hover:bg-white/80"
          }`}
        >
          {voiceMode ? <SpeakerHigh size={13} weight="fill" /> : <SpeakerSlash size={13} />}
          Voice {voiceMode ? "on" : "off"}
        </button>
        {voices.length > 0 && (
          <select
            value={voiceId}
            onChange={(e) => {
              const v = e.target.value;
              setVoiceId(v);
              try { window.localStorage.setItem(VOICE_PREF_KEY, v); } catch { /* private mode */ }
              track("personal_prayer_voice_changed", { voice_id: v });
              // If voice mode is on, replay current step in the new voice.
              if (voiceMode) { stopVoice(); setTimeout(playVoiceForStep, 60); }
            }}
            data-testid="voice-picker"
            aria-label="Guide voice"
            className="px-3 py-1.5 rounded-full bg-white/70 border border-[#E8DFD3] text-xs font-body text-[#5C4033] hover:bg-white/90"
          >
            {voices.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name} · {v.tone}
              </option>
            ))}
          </select>
        )}
        {MUSIC_AVAILABLE && (
          <button
            onClick={() => setMusicOn((v) => !v)}
            data-testid="music-toggle"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-body border ${
              musicOn ? "bg-[#FAF5EC] border-[#C4A265] text-[#5C4033]" : "bg-white/60 border-[#E8DFD3] text-[#5C4033] hover:bg-white/80"
            }`}
          >
            <MusicNote size={13} weight={musicOn ? "fill" : "regular"} />
            Music {musicOn ? "on" : "off"}
          </button>
        )}
        {MUSIC_AVAILABLE && musicOn && (
          <>
            <select
              value={musicMode}
              onChange={(e) => { setMusicMode(e.target.value); track("personal_prayer_music_mode", { mode: e.target.value }); }}
              data-testid="music-mode"
              className="px-2 py-1.5 rounded-full bg-white/70 border border-[#E8DFD3] text-xs font-body text-[#5C4033]"
            >
              {MUSIC_MODES.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
            <input
              type="range" min="0" max="0.5" step="0.02"
              value={musicVol}
              onChange={(e) => setMusicVol(Number(e.target.value))}
              data-testid="music-volume"
              className="accent-[#C4A265] w-24"
              aria-label="Music volume"
            />
          </>
        )}
      </div>

      {/* Body */}
      {isComplete ? (
        <div className="glass-card-strong p-7 text-center relative overflow-hidden" data-testid="journey-complete">
          <div className="spiritual-orb-sm w-44 h-44 -top-12 -right-10 animate-orb opacity-50" />
          <div className="relative z-10">
            <CheckCircle size={42} weight="fill" className="text-emerald-600 mx-auto mb-3" />
            <h2 className="font-heading text-2xl text-[#2D1B0E] font-medium mb-2">Amen.</h2>
            <p className="font-heading text-sm text-[#5C4033] italic mb-3 leading-relaxed max-w-md mx-auto">
              {j.closing}
            </p>
            <p className="text-[#8B7355] font-body text-xs mb-5">
              Your journey is saved. Return to it any time.
            </p>
            <div className="flex flex-wrap gap-2 justify-center">
              <button
                onClick={restart}
                data-testid="restart-btn"
                className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full glass-card text-[#5C4033] text-xs font-body font-medium hover:bg-white/60"
              >
                <ArrowCounterClockwise size={13} /> Walk it again
              </button>
              <Link to="/guided-prayer/new" data-testid="new-journey-btn"
                className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-[#2D1B0E] text-white text-xs font-body font-medium hover:bg-[#3D2B1F]">
                Begin a new prayer <ArrowRight size={12} />
              </Link>
            </div>
          </div>
        </div>
      ) : (
        <div className="glass-card-strong p-6 sm:p-7 mb-4 relative overflow-hidden" data-testid="step-card">
          <div className="spiritual-orb-sm w-40 h-40 -top-12 -right-10 animate-orb opacity-40" />
          <div className="relative z-10">
            {/* Progress */}
            <div className="flex items-center gap-1.5 mb-3" data-testid="step-progress">
              {Array.from({ length: total }).map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 rounded-full transition-all ${
                    i === step ? "w-7 bg-[#C4A265]" : i < step ? "w-3 bg-[#C4A265]/50" : "w-3 bg-[#D4C9BA]"
                  }`}
                />
              ))}
              <span className="text-[#8B7355] text-[10px] font-body ml-2">
                Step {step + 1} of {total} · {STEP_LABEL[cur.type] || cur.type}
              </span>
            </div>

            <h2 className="font-heading text-xl sm:text-2xl text-[#2D1B0E] font-medium mb-3 leading-snug">
              {cur.title}
            </h2>

            {cur.scripture?.text && (
              <div className="bg-white/60 border border-[#E8DFD3] rounded-xl p-4 mb-3" data-testid="step-scripture">
                <Quotes size={14} weight="fill" className="text-[#C4A265] mb-1" />
                <p className="font-heading text-sm sm:text-base italic text-[#2D1B0E] leading-relaxed mb-1">
                  "{cur.scripture.text}"
                </p>
                <p className="text-[#C4A265] font-body text-xs font-medium">— {cur.scripture.reference}</p>
              </div>
            )}

            <p className="font-body text-[15px] sm:text-base text-[#2D1B0E] leading-relaxed whitespace-pre-line" data-testid="step-body">
              {cur.body}
            </p>

            {/* Audio loading banner — visible whenever TTS fetch is in flight */}
            {audioLoading && (
              <div className="mt-4 flex items-center gap-3 px-4 py-3 rounded-xl bg-[#FAF5EC] border border-[#E8DFD3]" data-testid="audio-loading-banner">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FAF5EC] via-[#EDE6DC] to-[#E5D9C7] flex items-center justify-center shadow-inner flex-shrink-0">
                  <Sparkle size={18} weight="fill" className="text-[#C4A265] animate-pulse" />
                </div>
                <div className="min-w-0">
                  <p className="font-heading text-sm text-[#2D1B0E] font-medium leading-tight">
                    Generating audio…
                  </p>
                  <p className="font-body text-[11px] text-[#8B7355] italic mt-0.5">
                    Voice is preparing this step in a calm guide voice.
                  </p>
                </div>
                <span className="ml-auto inline-flex gap-1 flex-shrink-0">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C4A265] animate-pulse" />
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C4A265] animate-pulse" style={{ animationDelay: "150ms" }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C4A265] animate-pulse" style={{ animationDelay: "300ms" }} />
                </span>
              </div>
            )}

            {/* Voice replay button (always available; voice-mode auto-plays) */}
            <div className="flex items-center justify-between mt-5 flex-wrap gap-2">
              <button
                onClick={speaking || audioLoading ? stopVoice : playVoiceForStep}
                disabled={audioLoading}
                data-testid="speak-btn"
                className="inline-flex items-center gap-1.5 text-[#5C4033] text-xs font-body hover:text-[#2D1B0E] disabled:opacity-70"
              >
                {audioLoading ? (
                  <><Sparkle size={13} weight="fill" className="animate-pulse text-[#C4A265]" /> Generating audio…</>
                ) : speaking ? (
                  <><Pause size={13} weight="fill" /> Pause voice</>
                ) : (
                  <><Play size={13} weight="fill" /> Read aloud</>
                )}
              </button>
              <div className="inline-flex gap-2">
                <button
                  onClick={back}
                  disabled={step === 0}
                  data-testid="back-btn"
                  className="px-4 py-2 rounded-full glass-card text-[#5C4033] text-xs font-body hover:bg-white/60 disabled:opacity-40"
                >
                  Back
                </button>
                <button
                  onClick={advance}
                  data-testid="advance-btn"
                  className="px-5 py-2 rounded-full bg-[#2D1B0E] hover:bg-[#3D2B1F] text-white text-xs font-body font-medium inline-flex items-center gap-1.5"
                >
                  {step >= total - 1 ? "Amen — Finish" : "Continue"} <ArrowRight size={12} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer actions */}
      <div className="flex items-center justify-between text-xs font-body text-[#8B7355] mt-2">
        <span>Saved · returns to step {Math.min(step + 1, total)} of {total}</span>
        <button onClick={remove} data-testid="delete-btn" className="inline-flex items-center gap-1 text-[#B8A088] hover:text-rose-700">
          <Trash size={12} /> Delete
        </button>
      </div>
    </div>
  );
}
